import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { CommonActions } from '@react-navigation/native';

const APP_SHELL_ROUTE = 'AppShell';
const MAIN_TABS_ROUTE = 'MainTabs';

/** Destinos de MainTabs (no viven en ShellStack). Mantener alineado con MAIN_TABS_ROUTE_NAMES. */
const MAIN_TABS_DESTINATIONS = new Set([
  'Home',
  'POS',
  'Catalog',
  'Inventory',
  'Customers',
  'Reports',
  'CashManagement',
  'SalesList',
  'Expenses',
  'Purchases',
  'PromotionsList',
  'PriceListsList',
  'LoyaltyProgram',
  'SupportReports',
  'Profile',
  'OrdersInbox',
  'OnlineOrders',
  'OnlineComplaints',
  'Consultations',
  'Quotes',
  'Appointments',
  'WorkOrders',
  'DiningTables',
  'KitchenQueue',
  'AiChat',
  'More',
]);

type NavLike = {
  navigate: (name: string, params?: object) => void;
  dispatch?: (action: ReturnType<typeof CommonActions.navigate>) => void;
  getParent?: () => NavLike | undefined;
  /** RN may return undefined while the navigator is mounting. */
  getState?: () => { routeNames?: string[] } | undefined;
};

function navigateRouteWithMerge(nav: NavLike, name: string, params?: object): void {
  if (params != null && nav.dispatch) {
    nav.dispatch(
      CommonActions.navigate({
        name,
        params,
        merge: true,
      }),
    );
    return;
  }
  nav.navigate(name, params);
}

function navigateMainTabsScreen(nav: NavLike, screen: string, params?: object): void {
  const nestedParams =
    params != null
      ? {
          screen,
          params,
          merge: true,
        }
      : { screen };
  if (nav.dispatch) {
    nav.dispatch(
      CommonActions.navigate({
        name: MAIN_TABS_ROUTE,
        params: nestedParams,
        merge: true,
      }),
    );
    return;
  }
  nav.navigate(MAIN_TABS_ROUTE, nestedParams);
}

function isShellNavigator(nav: NavLike | undefined): boolean {
  if (!nav) return false;
  return nav.getState?.()?.routeNames?.includes(MAIN_TABS_ROUTE) === true;
}

function isMainTabsDestination(route: string): boolean {
  return MAIN_TABS_DESTINATIONS.has(route);
}

/**
 * Resuelve el ShellStack: desde tabs el padre es shell; desde Profile/Billing el propio nav ya es shell.
 * (No usar getParent() a ciegas: desde pantallas shell el padre es RootStack.)
 */
export function getShellNavigation(navigation: NavLike): NavLike {
  let current: NavLike | undefined = navigation;
  while (current) {
    if (isShellNavigator(current)) return current;
    current = current.getParent?.();
  }
  return navigation;
}

/** Navegador raíz (modales globales: soporte, selector sucursal, onboarding). */
export function getRootNavigation(navigation: NavLike): NavLike {
  let current: NavLike | undefined = navigation;
  while (current?.getParent?.()) {
    current = current.getParent();
  }
  return current ?? navigation;
}

export function navigateInShell(
  navigation: NavigationProp<ParamListBase> | NavLike,
  route: string,
  params?: object
): void {
  const nav = navigation as NavLike;

  // Caja, Ventas, etc. viven en MainTabs (tabs ocultos), no en ShellStack.
  if (isMainTabsDestination(route)) {
    if (nav.getState?.()?.routeNames?.includes(route)) {
      navigateRouteWithMerge(nav, route, params);
      return;
    }
    const parent = nav.getParent?.();
    if (parent?.getState?.()?.routeNames?.includes(route)) {
      navigateRouteWithMerge(parent, route, params);
      return;
    }
    const shellCandidate = getShellNavigation(nav);
    const shell = isShellNavigator(shellCandidate) ? shellCandidate : null;
    if (shell) {
      navigateMainTabsScreen(shell, route, params);
      return;
    }
    const root = getRootNavigation(nav);
    if (root.dispatch) {
      root.dispatch(
        CommonActions.navigate({
          name: APP_SHELL_ROUTE,
          params: {
            screen: MAIN_TABS_ROUTE,
            params:
              params != null
                ? { screen: route, params, merge: true }
                : { screen: route },
            merge: true,
          },
          merge: true,
        }),
      );
      return;
    }
    root.navigate(APP_SHELL_ROUTE, {
      screen: MAIN_TABS_ROUTE,
      params: {
        screen: route,
        ...(params != null ? { params, merge: true } : {}),
      },
    });
    return;
  }

  const shellCandidate = getShellNavigation(nav);
  const shell = isShellNavigator(shellCandidate) ? shellCandidate : null;

  if (shell) {
    shell.navigate(route, params);
    return;
  }

  getRootNavigation(nav).navigate(APP_SHELL_ROUTE, {
    screen: route,
    ...(params != null ? { params } : {}),
  });
}

export function navigateRoot(
  navigation: NavigationProp<ParamListBase> | NavLike,
  route: string,
  params?: object
): void {
  const root = getRootNavigation(navigation as NavLike);
  root.navigate(route, params);
}

/** Reemplaza el stack raíz (cierra modales como Onboarding) y deja una sola ruta activa. */
export function resetRootRoute(
  navigation: NavigationProp<ParamListBase> | NavLike,
  route: string,
  params?: object,
): void {
  const root = getRootNavigation(navigation as NavLike);
  if (root.dispatch) {
    root.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: route, ...(params != null ? { params } : {}) }],
      }),
    );
    return;
  }
  root.navigate(route, params);
}

/**
 * Cierra el overlay actual (p. ej. BranchSelector) y navega en el shell debajo.
 * Evita dejar modales transparentes encima de pantallas de Ajustes.
 */
export function dismissSelfThenNavigateInShell(
  navigation: NavigationProp<ParamListBase> | NavLike,
  screen: string,
  params?: object
): void {
  const nav = navigation as NavLike & { canGoBack?: () => boolean; goBack?: () => void };
  const root = getRootNavigation(nav);
  if (nav.canGoBack?.()) {
    nav.goBack!();
    queueMicrotask(() => {
      root.navigate(APP_SHELL_ROUTE, {
        screen,
        ...(params != null ? { params } : {}),
      });
    });
    return;
  }
  navigateInShell(navigation, screen, params);
}

/** Cierra el overlay actual y abre un modal root (p. ej. CreateBranch). */
export function dismissSelfThenNavigateRoot(
  navigation: NavigationProp<ParamListBase> | NavLike,
  route: string,
  params?: object
): void {
  const nav = navigation as NavLike & { canGoBack?: () => boolean; goBack?: () => void };
  const root = getRootNavigation(nav);
  if (nav.canGoBack?.()) {
    nav.goBack!();
    queueMicrotask(() => root.navigate(route, params));
    return;
  }
  navigateRoot(navigation, route, params);
}
